import { useEffect, useRef, useState } from "react";
import {
  isTimeoutError,
  JFETCH_DEFAULT_TIMEOUT_MS,
  reduceLoaded,
  type ResourceState,
} from "../resourceState";
import { connectOwnerEventStream } from "../eventStream";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import { dashboardStatusErrorCopy } from "./dashboardStatusErrorCopy";
import type { AgentOsStudioRole } from "./agentOsStudioModel";
import {
  api,
  hasSessionToken,
  type HealthInfo,
  type ConnectorStatusView,
  type ApprovalView,
  type CommandOverview,
  type CommandRun,
  type CommandToolRun,
  type CommandTimelineEvent,
  type CommandAgent,
  type CommandInfra,
  type LearningProposalView,
  type NotificationView,
  type OwnerDeviceView,
  type OwnerAuthStatusView,
  type ApprovalInboxView,
  type AgentRegistryView,
  type AgentOsStudioReadModelView,
  type OwnerMissionView,
  type WorkerSandboxStatusView,
  type RunnerJobBoundaryView,
  type GatedEvidenceView,
  type RuntimeReliabilityBaseline,
  type NotificationCenterItemView,
  type RoutineRunView,
  type RoutineView,
  type MemoryInjectPolicy,
  type MemoryKind,
  type MemoryRetention,
  type MemoryReviewStatus,
  type MemorySensitivity,
  type MemorySpace,
  type AgentBrainScopeEntryView,
  type AutonomousWorkQueueItemView,
  type AgentHarnessControlView,
  type BrainDecisionLogItem,
  type ControlPlaneSummary,
  ApiRequestError,
  isAbortError,
} from "../api";

/**
 * Redigierte Memory-Sicht fürs Always-On-Dashboard: NUR Key + Metadaten.
 * Der rohe `value` (kann Secrets/PII enthalten) wird bewusst NICHT in den
 * Dashboard-State übernommen — er bleibt der dedizierten Memory-Verwaltung
 * vorbehalten. SIMO-DASHBOARD-PANELS-LIVE-2.
 */
export interface MemoryEntryMeta {
  key: string;
  updatedAt: string;
  space: MemorySpace;
  kind: MemoryKind;
  sensitivity: MemorySensitivity;
  injectPolicy: MemoryInjectPolicy;
  retention: MemoryRetention;
  reviewStatus: MemoryReviewStatus;
  source: string | null;
  expiresAt: string | null;
  embeddingStatus: "current" | "stale" | "missing";
}

export interface DashboardData {
  hasSessionToken: boolean;
  /**
   * SIMO-CODEX-CLAUDE-EXECUTION-LANE-1 — S6: die serverseitige Session-Rolle.
   *
   * Sie ist die einzige Quelle fuer Mutations-Berechtigungen in der UI. `null`
   * bedeutet "noch nicht geladen oder keine Session" und ist fail-closed
   * read-only — nicht "darf alles".
   */
  sessionRole: AgentOsStudioRole | null;
  health: HealthInfo | null;
  connectors: ConnectorStatusView[];
  approvals: ApprovalView[];
  approvalInbox: ApprovalInboxView | null;
  approvalInboxError: Error | null;
  agentRegistry: AgentRegistryView | null;
  agentRegistryError: Error | null;
  agentOsStudio?: AgentOsStudioReadModelView | null;
  agentOsStudioError?: Error | null;
  ownerMission?: OwnerMissionView | null;
  ownerMissionError?: Error | null;
  agentHarnessControl?: AgentHarnessControlView | null;
  agentHarnessControlError?: Error | null;
  agentBrainScopes?: AgentBrainScopeEntryView[];
  agentBrainScopesError?: Error | null;
  workQueueItems?: AutonomousWorkQueueItemView[];
  workQueueError?: Error | null;
  /** S0 — Owner-Gate-Zaehler aus /api/agent-exchange/owner-gate-Lesepfad; nie Fake-0. */
  ownerGate?:
    | { state: "LOADING" }
    | { state: "LIVE"; openCount: number | null }
    | { state: "TIMEOUT" }
    | { state: "UNAVAILABLE" };
  /** OPNAV1 Slice 3 — kanonische Counter-/Run-Semantik (Chat-/Tool-Runs ausgeschlossen). */
  controlPlaneSummary?: ControlPlaneSummary | null;
  controlPlaneSummaryError?: Error | null;
  workerSandbox: WorkerSandboxStatusView | null;
  workerSandboxError: Error | null;
  runnerBoundary: RunnerJobBoundaryView | null;
  runnerBoundaryError: Error | null;
  auditOk: boolean | null;
  // SIMO-COMMAND-CENTER-READONLY-1 — echte Aggregat-Daten.
  overview: CommandOverview | null;
  runs: CommandRun[];
  timeline: CommandTimelineEvent[];
  agents: CommandAgent[];
  infra: CommandInfra | null;
  runtimeReliability: RuntimeReliabilityBaseline | null;
  // SIMO-DASHBOARD-PANELS-LIVE-2 — persistierte Tool-Runs + redigierte Memory-Sicht.
  toolRuns: CommandToolRun[];
  memory: MemoryEntryMeta[];
  memoryTotal: number;
  brainDecisionLog?: BrainDecisionLogItem[];
  brainDecisionLogError?: Error | null;
  routines?: RoutineView[];
  routineRuns?: RoutineRunView[];
  routinesError?: Error | null;
  /** true nur, wenn der Routine-Scheduler serverseitig wirklich laeuft. */
  routineSchedulerEnabled?: boolean;
  notifications: NotificationView[];
  notificationCenterItems: NotificationCenterItemView[];
  learningProposals: LearningProposalView[];
  gatedEvidence: GatedEvidenceView[];
  // SIMO-OMNICHANNEL-ACTIONS-B1 — Owner-Devices für die Geräte-Trust-Verwaltung.
  ownerDevices: OwnerDeviceView[];
  ownerAuthStatus: OwnerAuthStatusView | null;
  ownerAuthError: ApiRequestError | null;
  errors: Record<string, string>;
  refresh: () => void;
  /**
   * LOADING-TRUTH-FIX (S3): Zustandsmaschine je geladener Ressource
   * (IDLE→LOADING→LIVE_DATA|EMPTY_PROVEN|STALE|SOURCE_UNAVAILABLE|ERROR).
   * Optional, damit bestehende Test-Fixtures minimal bleiben.
   */
  resourceStates?: Record<string, ResourceState<unknown>>;
  /** Retry-Aktion bei TIMEOUT/ERROR/SOURCE_UNAVAILABLE (Neuladen). */
  retryResource?: (key: string) => void;
}

export function dashboardDataErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  return ownerFacingGlobalError(message).slice(0, 240);
}

/**
 * Read-only Live-Monitoring-Daten für die Command-Center-Panels.
 *
 * SIMO-COMMAND-CENTER-LIVE-EVENTS-2: primär getrieben vom SSE-Live-Stream
 * (`api.commandStream`) — ein `snapshot`-Event aktualisiert die KPIs sofort und
 * triggert ein Refresh der übrigen Panels. Das bisherige Intervall-Polling
 * bleibt als langsamer Backstop/Fallback erhalten (greift, wenn SSE fehlt oder
 * abbricht). Resilient: letzter Stand bleibt bei Fehler erhalten. Kein
 * Write-Scope.
 */
const SESSION_ROLES: ReadonlyArray<AgentOsStudioRole> = ["owner", "admin", "operator", "readonly"];

/**
 * Nur bekannte Rollen zaehlen. Eine unbekannte oder fehlende `roleId` wird zu
 * `null` und damit read-only, statt versehentlich als berechtigt zu gelten.
 */
function normalizeSessionRole(roleId: unknown): AgentOsStudioRole | null {
  return typeof roleId === "string" && (SESSION_ROLES as readonly string[]).includes(roleId)
    ? roleId as AgentOsStudioRole
    : null;
}

export function useDashboardData(pollMs = 30000): DashboardData {
  // S0.3: Supersede-Register fuer den EINEN autoritativen ownerGate-Request.
  const ownerGateAbort = useRef<AbortController | null>(null);
  const ownerGateSeqRef = useRef(0);
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [connectors, setConnectors] = useState<ConnectorStatusView[]>([]);
  const [approvals, setApprovals] = useState<ApprovalView[]>([]);
  const [approvalInbox, setApprovalInbox] = useState<ApprovalInboxView | null>(null);
  const [approvalInboxError, setApprovalInboxError] = useState<Error | null>(null);
  const [agentRegistry, setAgentRegistry] = useState<AgentRegistryView | null>(null);
  const [agentRegistryError, setAgentRegistryError] = useState<Error | null>(null);
  const [agentOsStudio, setAgentOsStudio] = useState<AgentOsStudioReadModelView | null>(null);
  const [agentOsStudioError, setAgentOsStudioError] = useState<Error | null>(null);
  const [ownerMission, setOwnerMission] = useState<OwnerMissionView | null>(null);
  const [ownerMissionError, setOwnerMissionError] = useState<Error | null>(null);
  const [agentHarnessControl, setAgentHarnessControl] = useState<AgentHarnessControlView | null>(null);
  const [agentHarnessControlError, setAgentHarnessControlError] = useState<Error | null>(null);
  const [agentBrainScopes, setAgentBrainScopes] = useState<AgentBrainScopeEntryView[]>([]);
  const [agentBrainScopesError, setAgentBrainScopesError] = useState<Error | null>(null);
  const [workQueueItems, setWorkQueueItems] = useState<AutonomousWorkQueueItemView[]>([]);
  const [workQueueError, setWorkQueueError] = useState<Error | null>(null);
  const [controlPlaneSummary, setControlPlaneSummary] = useState<ControlPlaneSummary | null>(null);
  // S0 — Owner-Gate-Zustand: LOADING/UNAVAILABLE statt Fake-0 (2E).
  const [ownerGate, setOwnerGate] = useState<
    | { state: "LOADING" }
    | { state: "LIVE"; openCount: number | null }
    | { state: "TIMEOUT" }
    | { state: "UNAVAILABLE" }
  >({ state: "LOADING" });
  const [controlPlaneSummaryError, setControlPlaneSummaryError] = useState<Error | null>(null);
  const [workerSandbox, setWorkerSandbox] = useState<WorkerSandboxStatusView | null>(null);
  const [workerSandboxError, setWorkerSandboxError] = useState<Error | null>(null);
  const [runnerBoundary, setRunnerBoundary] = useState<RunnerJobBoundaryView | null>(null);
  const [runnerBoundaryError, setRunnerBoundaryError] = useState<Error | null>(null);
  const [auditOk, setAuditOk] = useState<boolean | null>(null);
  const [overview, setOverview] = useState<CommandOverview | null>(null);
  const [runs, setRuns] = useState<CommandRun[]>([]);
  const [timeline, setTimeline] = useState<CommandTimelineEvent[]>([]);
  const [agents, setAgents] = useState<CommandAgent[]>([]);
  const [infra, setInfra] = useState<CommandInfra | null>(null);
  const [runtimeReliability, setRuntimeReliability] = useState<RuntimeReliabilityBaseline | null>(null);
  const [toolRuns, setToolRuns] = useState<CommandToolRun[]>([]);
  const [memory, setMemory] = useState<MemoryEntryMeta[]>([]);
  const [memoryTotal, setMemoryTotal] = useState(0);
  const [brainDecisionLog, setBrainDecisionLog] = useState<BrainDecisionLogItem[]>([]);
  const [brainDecisionLogError, setBrainDecisionLogError] = useState<Error | null>(null);
  const [routines, setRoutines] = useState<RoutineView[]>([]);
  const [routineRuns, setRoutineRuns] = useState<RoutineRunView[]>([]);
  const [routinesError, setRoutinesError] = useState<Error | null>(null);
  const [routineSchedulerEnabled, setRoutineSchedulerEnabled] = useState(false);
  const [notifications, setNotifications] = useState<NotificationView[]>([]);
  const [notificationCenterItems, setNotificationCenterItems] = useState<NotificationCenterItemView[]>([]);
  const [learningProposals, setLearningProposals] = useState<LearningProposalView[]>([]);
  const [gatedEvidence, setGatedEvidence] = useState<GatedEvidenceView[]>([]);
  const [ownerDevices, setOwnerDevices] = useState<OwnerDeviceView[]>([]);
  const [ownerAuthStatus, setOwnerAuthStatus] = useState<OwnerAuthStatusView | null>(null);
  const [ownerAuthError, setOwnerAuthError] = useState<ApiRequestError | null>(null);
  const [sessionRole, setSessionRole] = useState<AgentOsStudioRole | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // LOADING-TRUTH-FIX (S3): Per-Ressource-Zustandsmaschine (s. resourceState.ts).
  const [resourceStates, setResourceStates] = useState<Record<string, ResourceState<unknown>>>({});
  const [tick, setTick] = useState(0);
  const sessionTokenPresent = hasSessionToken();

  useEffect(() => {
    let cancelled = false;
    // Ladungs-Sequenz je Ressource: spaet angekommene Antworten aelterer
    // Ladungen werden als STALE gekennzeichnet, nicht als LIVE (S3/S5).
    const seqByKey: Record<string, number> = {};
    const remember = (key: string, err: unknown): void => {
      if (cancelled) return;
      setErrors((current) => ({ ...current, [key]: dashboardDataErrorMessage(err) }));
    };
    const clear = (key: string): void => {
      if (cancelled) return;
      setErrors((current) => {
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });
    };
    const resetSessionBoundState = (): void => {
      if (cancelled) return;
      setHealth(null);
      setConnectors([]);
      setApprovals([]);
      setApprovalInbox(null);
      setApprovalInboxError(null);
      setAgentRegistry(null);
      setAgentRegistryError(null);
      setAgentOsStudio(null);
      setAgentOsStudioError(null);
      setAgentHarnessControl(null);
      setAgentHarnessControlError(null);
      setAgentBrainScopes([]);
      setAgentBrainScopesError(null);
      setWorkQueueItems([]);
      setWorkQueueError(null);
      setControlPlaneSummary(null);
      setControlPlaneSummaryError(null);
      setWorkerSandbox(null);
      setWorkerSandboxError(null);
      setRunnerBoundary(null);
      setRunnerBoundaryError(null);
      setAuditOk(null);
      setOverview(null);
      setRuns([]);
      setTimeline([]);
      setAgents([]);
      setInfra(null);
      setRuntimeReliability(null);
      setToolRuns([]);
      setMemory([]);
      setMemoryTotal(0);
      setBrainDecisionLog([]);
      setBrainDecisionLogError(null);
      setRoutines([]);
      setRoutineRuns([]);
      setRoutinesError(null);
      setRoutineSchedulerEnabled(false);
      setNotifications([]);
      setNotificationCenterItems([]);
      setLearningProposals([]);
      setGatedEvidence([]);
      setOwnerDevices([]);
      setOwnerAuthStatus(null);
      setOwnerAuthError(null);
      setSessionRole(null);
      setErrors({});
    };
    const load = (): void => {
      if (!sessionTokenPresent) {
        resetSessionBoundState();
        return;
      }
      // LOADING-TRUTH-FIX (S3): Per-Ressource-Zustandsmaschine
      // IDLE→LOADING→LIVE_DATA|EMPTY_PROVEN|STALE|SOURCE_UNAVAILABLE|ERROR.
      // `track` ist Pass-through: die bestehenden .then/.catch-Ketten bleiben
      // unangetastet; track fuehrt nur die Zustandsbuchhaltung (inkl.
      // Late-Arrival-Kennzeichnung als STALE, nie als LIVE).
      const track = <T,>(key: string, source: string, promise: Promise<T>, isEmpty: (value: T) => boolean = () => false): Promise<T> => {
        const seq = (seqByKey[key] = (seqByKey[key] ?? 0) + 1);
        const startedAt = Date.now();
        if (!cancelled) {
          setResourceStates((prev) => ({
            ...prev,
            [key]: reduceLoaded(prev[key], { kind: "loading", startedAt, timeoutAt: startedAt + JFETCH_DEFAULT_TIMEOUT_MS, source }),
          }));
        }
        const isLate = (): boolean => seqByKey[key] !== seq;
        return promise.then(
          (value) => {
            if (!cancelled) {
              setResourceStates((prev) => ({
                ...prev,
                [key]: reduceLoaded(prev[key], { kind: "success", value, isEmpty: isEmpty(value), finishedAt: Date.now(), late: isLate() }),
              }));
            }
            return value;
          },
          (error) => {
            if (!cancelled) {
              setResourceStates((prev) => ({
                ...prev,
                [key]: reduceLoaded(prev[key], { kind: "error", error, finishedAt: Date.now(), late: isLate() }),
              }));
            }
            throw error;
          },
        );
      };
      void track("health", "/api/health", api.health()).then((h) => { if (!cancelled) setHealth(h); clear("health"); }).catch((e) => remember("health", e));
      // S6: die Rolle entscheidet ueber Mutations-Oberflaechen. Faellt der Abruf
      // aus, bleibt sie null und die UI damit read-only — fail-closed.
      void api.session().then((s) => {
        if (cancelled) return;
        setSessionRole(normalizeSessionRole(s.user?.roleId));
        clear("session");
      }).catch((e) => {
        if (!cancelled) setSessionRole(null);
        remember("session", e);
      });
      void track("connectors", "/api/connectors", api.listConnectors(), (r) => r.items.length === 0).then((r) => { if (!cancelled) setConnectors(r.items); clear("connectors"); }).catch((e) => remember("connectors", e));
      void api.listApprovals().then((r) => { if (!cancelled) setApprovals(r.approvals); clear("approvals"); }).catch((e) => remember("approvals", e));
      void api.approvalInbox().then((r) => {
        if (cancelled) return;
        setApprovalInbox(r);
        setApprovalInboxError(null);
        clear("approvalInbox");
      }).catch((e) => {
        if (cancelled) return;
        setApprovalInboxError(new Error(dashboardStatusErrorCopy(e, "approval-inbox")));
        remember("approvalInbox", e);
      });
      void api.agentRegistry().then((r) => {
        if (cancelled) return;
        setAgentRegistry(r);
        setAgentRegistryError(null);
        clear("agentRegistry");
      }).catch((e) => {
        if (cancelled) return;
        setAgentRegistryError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("agentRegistry", e);
      });
      void api.agentOsStudio().then((r) => {
        if (cancelled) return;
        setAgentOsStudio(r);
        setAgentOsStudioError(null);
        clear("agentOsStudio");
      }).catch((e) => {
        if (cancelled) return;
        setAgentOsStudioError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("agentOsStudio", e);
      });
      void api.agentOrbMissionCurrent().then((r) => {
        if (cancelled) return;
        setOwnerMission(r.mission);
        setOwnerMissionError(null);
        clear("ownerMission");
      }).catch((e) => {
        if (cancelled) return;
        setOwnerMissionError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("ownerMission", e);
      });
      void track("agentHarnessControl", "/api/agent-harness/control", api.agentHarnessControl()).then((r) => {
        if (cancelled) return;
        setAgentHarnessControl(r);
        setAgentHarnessControlError(null);
        clear("agentHarnessControl");
      }).catch((e) => {
        if (cancelled) return;
        setAgentHarnessControlError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("agentHarnessControl", e);
      });
      void api.listAgentBrainScopes().then((r) => {
        if (cancelled) return;
        setAgentBrainScopes(r.scopes);
        setAgentBrainScopesError(null);
        clear("agentBrainScopes");
      }).catch((e) => {
        if (cancelled) return;
        setAgentBrainScopesError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("agentBrainScopes", e);
      });
      void api.listWorkQueue(20).then((r) => {
        if (cancelled) return;
        setWorkQueueItems(r.items);
        setWorkQueueError(null);
        clear("workQueue");
      }).catch((e) => {
        if (cancelled) return;
        setWorkQueueError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("workQueue", e);
      });
      // OPNAV1 Slice 3 — kanonische Counter-/Mission-/Run-/Lease-Wahrheit.
      // Fehler werden NICHT durch 0 ersetzt: `controlPlaneSummary` bleibt dann
      // null und Oberflaechen zeigen UNAVAILABLE statt Fake-0.
      void api.controlPlaneSummary().then((r) => {
        if (cancelled) return;
        setControlPlaneSummary(r);
        setControlPlaneSummaryError(null);
        clear("controlPlaneSummary");
      }).catch((e) => {
        if (cancelled) return;
        setControlPlaneSummaryError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("controlPlaneSummary", e);
      });
      // S0 — Gate-Zaehler aus dem neuen Gate-Lesepfad (nicht aus der Summary
      // geraten): LIVE liefert die Zahl, alles andere bleibt LOADING/"—".
      // S0.3 — ABORT-SEMANTIK + REFRESH-DISZIPLIN: EIN autoritativer
      // In-Flight-Request — ein neuer Request superseded den alten (abort,
      // still, KEIN Fehler-State); ein AbortError ist NIEMALS Anzeige-Zustand
      // (Vorfall 2026-09-18: roher "user aborted" als Inbox-Fehler). Nur ein
      // echtes, klassifiziertes Timeout => TIMEOUT. Spaet-Antworten nach
      // Supersede werden per Sequence-Guard verworfen.
      if (ownerGateAbort.current) ownerGateAbort.current.abort();
      const ownerGateController = new AbortController();
      ownerGateAbort.current = ownerGateController;
      const ownerGateSeq = ownerGateSeqRef.current + 1;
      ownerGateSeqRef.current = ownerGateSeq;
      void track("ownerGate", "/api/agent-exchange/owner-gate", api.ownerGateStatus({ signal: ownerGateController.signal }))
        .then((r) => {
          if (cancelled || ownerGateSeqRef.current !== ownerGateSeq) return;
          if (r.state === "LIVE") setOwnerGate({ state: "LIVE", openCount: r.openCount });
          else if (r.state === "WARMING") setOwnerGate({ state: "LOADING" });
          else setOwnerGate({ state: "UNAVAILABLE" });
        })
        .catch((e) => {
          if (cancelled || ownerGateSeqRef.current !== ownerGateSeq) return;
          if (isAbortError(e)) return;
          if (isTimeoutError(e)) setOwnerGate({ state: "TIMEOUT" });
          else setOwnerGate({ state: "UNAVAILABLE" });
        })
        .finally(() => {
          if (ownerGateAbort.current === ownerGateController) ownerGateAbort.current = null;
        });
      void api.workerSandboxStatus().then((r) => {
        if (cancelled) return;
        setWorkerSandbox(r);
        setWorkerSandboxError(null);
        clear("workerSandbox");
      }).catch((e) => {
        if (cancelled) return;
        setWorkerSandboxError(new Error(dashboardStatusErrorCopy(e, "worker-status")));
        remember("workerSandbox", e);
      });
      void api.runnerJobBoundary().then((r) => {
        if (cancelled) return;
        setRunnerBoundary(r);
        setRunnerBoundaryError(null);
        clear("runnerBoundary");
      }).catch((e) => {
        if (cancelled) return;
        setRunnerBoundaryError(new Error(dashboardStatusErrorCopy(e, "worker-runner")));
        remember("runnerBoundary", e);
      });
      void api.verifyAudit().then((r) => { if (!cancelled) setAuditOk(r.ok); clear("audit"); }).catch((e) => remember("audit", e));
      void track("overview", "/api/command-center/overview", api.commandOverview()).then((o) => { if (!cancelled) setOverview(o); clear("overview"); }).catch((e) => remember("overview", e));
      void api.commandRuns({ limit: 12 }).then((r) => { if (!cancelled) setRuns(r.runs); clear("runs"); }).catch((e) => remember("runs", e));
      void api.commandTimeline(40).then((r) => { if (!cancelled) setTimeline(r.events); clear("timeline"); }).catch((e) => remember("timeline", e));
      void api.commandAgents().then((r) => { if (!cancelled) setAgents(r.agents); clear("agents"); }).catch((e) => remember("agents", e));
      void track("infra", "/api/command-center/infra", api.commandInfra()).then((i) => { if (!cancelled) setInfra(i); clear("infra"); }).catch((e) => remember("infra", e));
      void api.runtimeReliabilityBaseline().then((r) => { if (!cancelled) setRuntimeReliability(r); clear("runtimeReliability"); }).catch((e) => remember("runtimeReliability", e));
      void api.commandTools(20).then((r) => { if (!cancelled) setToolRuns(r.tools); clear("tools"); }).catch((e) => remember("tools", e));
      void api.listNotifications().then((r) => {
        if (!cancelled) {
          setNotifications(r.notifications);
          setNotificationCenterItems(r.centerItems ?? []);
        }
        clear("notifications");
      }).catch((e) => remember("notifications", e));
      void api.listLearningProposals().then((r) => { if (!cancelled) setLearningProposals(r.proposals); clear("learning"); }).catch((e) => remember("learning", e));
      void api.listArmedEvidence(24).then((r) => { if (!cancelled) setGatedEvidence(r.evidence); clear("gatedEvidence"); }).catch((e) => remember("gatedEvidence", e));
      void api.listOwnerDevices().then((r) => { if (!cancelled) setOwnerDevices(r.devices); clear("ownerDevices"); }).catch((e) => remember("ownerDevices", e));
      void api.ownerAuthStatus().then((r) => {
        if (cancelled) return;
        setOwnerAuthStatus(r);
        setOwnerAuthError(null);
        clear("ownerAuth");
      }).catch((e) => {
        if (cancelled) return;
        setOwnerAuthError(e instanceof ApiRequestError ? e : null);
        remember("ownerAuth", e);
      });
      // Memory: value-freie Projektion — der rohe `value` verlässt den Server
      // gar nicht erst (Defense-in-Depth, siehe /api/memory/meta).
      void api.listMemoryMeta().then((r) => {
        if (cancelled) return;
        setMemoryTotal(r.total);
        setMemory(r.entries);
        clear("memory");
      }).catch((e) => remember("memory", e));
      void api.listBrainDecisionLog(20).then((r) => {
        if (cancelled) return;
        setBrainDecisionLog(r.items);
        setBrainDecisionLogError(null);
        clear("brainDecisionLog");
      }).catch((e) => {
        if (cancelled) return;
        setBrainDecisionLogError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("brainDecisionLog", e);
      });
      void Promise.all([api.listRoutines(), api.listRoutineRuns(20)]).then(([routineResult, runResult]) => {
        if (cancelled) return;
        setRoutines(routineResult.items);
        setRoutineRuns(runResult.items);
        setRoutineSchedulerEnabled(routineResult.schedulerEnabled === true);
        setRoutinesError(null);
        clear("routines");
      }).catch((e) => {
        if (cancelled) return;
        setRoutinesError(new Error(dashboardStatusErrorCopy(e, "agent-registry")));
        remember("routines", e);
      });
    };

    // Initial + langsamer Backstop (greift erst mit SIMO-Session).
    load();
    // Backstop-Polling pausiert bei verstecktem Tab (Mobile-Hintergrund/Akku);
    // SSE ist Primaerquelle, beim Wiedersichtbarwerden greift der naechste Tick.
    // (SIMO-UI-MOBILE-RESPONSIVE-AND-ESCAPE-PASS-1)
    const tick = (): void => {
      if (typeof document !== "undefined" && document.hidden) return;
      load();
    };
    const id = window.setInterval(tick, pollMs);

    // SSE-Live-Stream: bei Aktivitätsänderung sofort die KPIs setzen und die
    // übrigen Panels refreshen. Bricht der Stream ab oder fehlt er, bleibt der
    // Backstop oben aktiv (kein Datenausfall).
    const ac = new AbortController();
    if (sessionTokenPresent) {
      void (async () => {
        try {
          for await (const { event, data } of api.commandStream(ac.signal)) {
            if (cancelled) break;
            if (event === "snapshot") {
              const ov = data.overview as CommandOverview | undefined;
              if (ov) setOverview(ov);
              load();
            }
            // `heartbeat`/`error`: Keep-Alive bzw. Backstop übernimmt — kein Sonderfall.
          }
        } catch (e) {
          remember("command-stream", e);
          // SSE nicht verfügbar/abgebrochen: Backstop-Polling trägt weiter.
        }
      })();
    }

    // SIMO-CROSS-DEVICE-SYNC-1: owner-scoped live event bus. Event-driven
    // (faster than the 2s change-poll) and covers device.changed, which the
    // command-stream activity signature does not. Every event triggers a
    // snapshot resync (load()); onOpen resyncs too. Additive to the backstop
    // and the command-stream — load() is idempotent, so overlap is harmless.
    const ownerStream = sessionTokenPresent
      ? connectOwnerEventStream({
          onOpen: () => {
            clear("ownerEventsStream");
            if (!cancelled) load();
          },
          onEvent: () => { if (!cancelled) load(); },
          onError: (e) => remember("ownerEventsStream", e),
        })
      : null;

    return () => {
      cancelled = true;
      window.clearInterval(id);
      ac.abort();
      ownerGateAbort.current?.abort();
      ownerStream?.close();
    };
  }, [pollMs, sessionTokenPresent, tick]);

    return {
    hasSessionToken: sessionTokenPresent,
    sessionRole,
    ownerGate,
    health, connectors, approvals, approvalInbox, approvalInboxError, agentRegistry, agentRegistryError, agentHarnessControl, agentHarnessControlError, agentBrainScopes, agentBrainScopesError, workQueueItems, workQueueError, controlPlaneSummary, controlPlaneSummaryError, workerSandbox, workerSandboxError, runnerBoundary, runnerBoundaryError, auditOk,
    overview, runs, timeline, agents, infra, runtimeReliability,
    agentOsStudio, agentOsStudioError, ownerMission, ownerMissionError,
    toolRuns, memory, memoryTotal, brainDecisionLog, brainDecisionLogError, routines, routineRuns, routinesError, routineSchedulerEnabled, notifications, notificationCenterItems, learningProposals, gatedEvidence,
    ownerDevices, ownerAuthStatus, ownerAuthError,
    errors,
    refresh: () => setTick((t) => t + 1),
    resourceStates,
    // Retry bei TIMEOUT/ERROR/SOURCE_UNAVAILABLE: Neuladen (load ist idempotent).
    retryResource: (_key: string) => setTick((t) => t + 1),
  };
}
